import { Minus, Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useQualityDetailContext } from '@/contexts/QualityContext';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { cn } from '@/lib/utils';
import useCognitoUsers from '@/hooks/useCognitoUsers';
import { filterUsersByRole } from '@/lib/approvals';

export const YES_NO_OPTIONS = [
  {
    label: 'Yes',
    value: 1,
  },
  {
    label: 'No, please justify',
    value: 0,
  },
];

const PlanCheckPanel = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form, setFormByKey } = useQualityDetailContext();
  const { planActionReason } = form;

  const planChecked = form.planChecked !== undefined ? Number(form.planChecked) : undefined;

  return (
    <Card className="mb-4">
      <CardHeader className="p-4 grow">
        <div className="flex gap-4 text-sm">
          <div className="grow content-center">Are there Action Plan(s)?</div>
          <div className="basis-40">
            {mode === DETAIL_MODE.STATIC ? (
              <div className="content-center">
                {planChecked === 1 ? 'Yes' : 'No'}
              </div>
            ) : (
              <Select
                onChange={({ target }) =>
                  setFormByKey('planChecked', target.value)
                }
                value={planChecked}
              >
                {planChecked === undefined && <option value="">-</option>}
                {YES_NO_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      {planChecked === 0 && (
        <CardContent>
          <div className="text-sm font-medium text-muted-foreground mb-2">
            Justifications for no Action Plans
          </div>
          {mode === DETAIL_MODE.STATIC ? (
            planActionReason
          ) : (
            <Textarea
              className="font-mono resize-none"
              onChange={({ target }) =>
                setFormByKey('planActionReason', target.value)
              }
              placeholder="Reason for no action plan..."
              rows={2}
              value={planActionReason}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
};

const genActionPlan = () => {
  return {
    account: '',
    description: '',
    id: +new Date(),
    implementAt: '',
  };
};

const PlanActionsPanel = ({ qaUsers = [] }) => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form, setFormByKey } = useQualityDetailContext();
  const planChecked = form.planChecked !== undefined ? Number(form.planChecked) : undefined;
  const planActions = form.planActions || [genActionPlan()];

  const handleAddPlan = () => {
    setFormByKey('planActions', [...planActions, genActionPlan()]);
  };

  const handleDeletePlan = (id) => {
    setFormByKey(
      'planActions',
      planActions.filter((item) => item.id !== id)
    );
  };

  const handleUpdatePlan = (id, fieldName, value) => {
    const result = planActions.map((planItem) => {
      if (planItem.id === id) return { ...planItem, [fieldName]: value };

      return { ...planItem };
    });

    setFormByKey('planActions', result);
  };

  return (
    <div className={cn(planChecked !== 1 && 'opacity-50 pointer-events-none')}>
      <div className={'rounded-md border'}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>S/N</TableHead>
              <TableHead>Action Plan / Corrective Action Description</TableHead>
              <TableHead className="w-52">Responsible Person</TableHead>
              <TableHead className="w-64">Target Implementation Date</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {planActions.map((planItem) => (
              <TableRow key={planItem.id}>
                <TableCell>{planItem.id}</TableCell>
                <TableCell>
                  {mode === DETAIL_MODE.STATIC ? (
                    planItem.description
                  ) : (
                    <Input
                      onChange={({ target }) =>
                        handleUpdatePlan(
                          planItem.id,
                          'description',
                          target.value
                        )
                      }
                      value={planItem.description}
                      required
                    />
                  )}
                </TableCell>
                <TableCell>
                  {mode === DETAIL_MODE.STATIC ? (
                    qaUsers.find(({ sub }) => sub === planItem.account)?.name || planItem.account
                  ) : (
                    <Select
                      onChange={({ target }) =>
                        handleUpdatePlan(planItem.id, 'account', target.value)
                      }
                      value={planItem.account}
                      required
                    >
                      <option value="">-</option>
                      {qaUsers.map(({ sub, name }) => (
                        <option key={sub} value={sub}>
                          {name}
                        </option>
                      ))}
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {mode === DETAIL_MODE.STATIC ? (
                    planItem.implementAt
                  ) : (
                    <Input
                      onChange={({ target }) =>
                        handleUpdatePlan(
                          planItem.id,
                          'implementAt',
                          target.value
                        )
                      }
                      type="date"
                      value={planItem.implementAt}
                      required
                    />
                  )}
                </TableCell>
                <TableCell>
                  {mode !== DETAIL_MODE.STATIC && (
                    <Button
                      onClick={() => handleDeletePlan(planItem.id)}
                      variant="outline"
                    >
                      <Minus />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {mode !== DETAIL_MODE.STATIC && (
        <Button className="mt-4" onClick={handleAddPlan} variant="outline">
          <Plus />
        </Button>
      )}
    </div>
  );
};

const PlanRequiredPanel = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form, setFormByKey } = useQualityDetailContext();
  const { planECReason } = form;
  const planRequired = form.planRequired !== undefined ? Number(form.planRequired) : undefined;

  return (
    <Card className="mb-4">
      <CardHeader className="p-4 grow">
        <div className="flex gap-4 text-sm">
          <div className="grow content-center">
            Effectiveness Check Required?
          </div>
          <div className="basis-40">
            {mode === DETAIL_MODE.STATIC ? (
              planRequired === 1 ? (
                'Yes'
              ) : (
                'No'
              )
            ) : (
              <Select
                onChange={({ target }) =>
                  setFormByKey('planRequired', target.value)
                }
                value={planRequired}
              >
                {planRequired === undefined && <option value="">-</option>}
                {YES_NO_OPTIONS.map(({ label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </div>
        </div>
      </CardHeader>
      {planRequired === 0 && (
        <CardContent>
          <div className="text-sm font-medium text-muted-foreground mb-2">
            Justifications for no Effectiveness Checks
          </div>
          {mode === DETAIL_MODE.STATIC ? (
            planECReason
          ) : (
            <Textarea
              className="font-mono resize-none"
              onChange={({ target }) =>
                setFormByKey('planECReason', target.value)
              }
              placeholder="Reason for no EC..."
              rows={2}
              value={planECReason}
            />
          )}
        </CardContent>
      )}
    </Card>
  );
};

const PlanCriteriaPanel = ({ qaUsers = [] }) => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form, setFormByKey } = useQualityDetailContext();
  const { planCriteriaEffectiveness, planCriteriaQA } = form;
  const planRequired = form.planRequired !== undefined ? Number(form.planRequired) : undefined;

  return (
    <div className={cn(planRequired !== 1 && 'opacity-50 pointer-events-none')}>
      <Card className="mb-4">
        <CardHeader className="p-4 grow">
          <div className="flex gap-4">
            <CardTitle className="pt-2 text-sm font-normal grow">
              Effectiveness Check Criteria
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {mode === DETAIL_MODE.STATIC ? (
            planCriteriaEffectiveness
          ) : (
            <Textarea
              className="font-mono resize-none"
              onChange={({ target }) =>
                setFormByKey('planCriteriaEffectiveness', target.value)
              }
              placeholder="Enter criteria..."
              rows={2}
              value={planCriteriaEffectiveness}
            />
          )}
        </CardContent>
      </Card>
      <Card className="mb-4">
        <CardHeader className="p-4 grow">
          <div className="flex gap-4">
            <CardTitle className="pt-2 text-sm font-normal grow">
              Assigned QA for Verification
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {mode === DETAIL_MODE.STATIC ? (
            qaUsers.find(({ sub }) => sub === planCriteriaQA)?.name || planCriteriaQA
          ) : (
            <Select
              onChange={({ target }) => {
                setFormByKey('planCriteriaQA', target.value);
              }}
              value={planCriteriaQA || ''}
            >
              {!planCriteriaQA && <option value="">-</option>}
              {qaUsers.map(({ sub, name }) => (
                <option key={sub} value={sub}>
                  {name}
                </option>
              ))}
            </Select>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const PlanFilePanel = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form, setFormByKey } = useQualityDetailContext();
  const { planFile } = form;

  return (
    <Card className="mb-4">
      <CardHeader className="p-4 grow">
        <div className="flex gap-4">
          <CardTitle className="pt-2 text-sm font-normal grow">
            File Location
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        {mode === DETAIL_MODE.STATIC ? (
          planFile
        ) : (
          <Textarea
            className="font-mono resize-none"
            onChange={({ target }) => setFormByKey('planFile', target.value)}
            placeholder="Enter file location or URL..."
            rows={2}
            value={planFile}
          />
        )}
      </CardContent>
    </Card>
  );
};

const QualityPlanView = () => {
  const { users } = useCognitoUsers();
  const qaUsers = filterUsersByRole(users, 'qmrQa');

  return (
    <div className="mb-4 space-y-4">
      <PlanCheckPanel />
      <PlanActionsPanel qaUsers={qaUsers} />
      <PlanRequiredPanel />
      <PlanCriteriaPanel qaUsers={qaUsers} />
      <PlanFilePanel />
    </div>
  );
};

export default QualityPlanView;
